// Шрифт подключается в styles.css через @font-face из public/fonts, а не пакетом
// @fontsource-variable/inter: тот при сабсеттинге вырезает character variants, а нам
// нужен cv05 (строчная `l` с хвостиком) — половина того, чем начертание Tana отличается
// от обычного Inter. Подробности — в шапке styles.css.
import "./styles.css";

import { idbDriver, Store } from "@mapcraft-tasks/client-store";
import { StoreProvider } from "@mapcraft-tasks/client-store/react";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { syncStatusBarColor } from "./lib/mobile";
import { initSessionWatch, probeSession } from "./lib/session";
import { SessionGate } from "./lib/session-gate";
import { syncEvents, transport } from "./lib/trpc";
import { router } from "./router";

/**
 * Точка входа. Здесь только проводка: стор, воркер, наблюдение за сессией — дальше
 * приложение не знает про сеть вообще.
 *
 * Ничего сетевого на верхнем уровне НЕ ждём. Раньше здесь стоял `await currentSession()`,
 * и в PWA без связи `fetch` реджектился, исключение вылетало из модуля, `render()` не
 * звался вовсе — отсюда и брался пустой экран цвета фона. Что показать вместо приложения,
 * решает `SessionGate`, уже внутри React.
 */

restoreTheme();

// Стор поднимается ВСЕГДА и первым делом: данные он берёт с диска, а не из сети, и ждать
// ответа сервера, чтобы показать своё же дерево, ему незачем.
const store = new Store({ transport, events: syncEvents, persistence: idbDriver() });
registerServiceWorker();
initSessionWatch();

// Дебаунс контентных правок хорош ровно до момента, когда вкладку закрывают:
// правка, которая ещё не ушла, обязана уйти сейчас (offline-sync §4).
document.addEventListener("visibilitychange", () => {
	if (document.visibilityState === "hidden") {
		void store.flush();
	}
});
window.addEventListener("pagehide", () => void store.flush());

void store.start();
void probeSession();

createRoot(document.getElementById("root") as HTMLElement).render(
	<StrictMode>
		<StoreProvider store={store}>
			<SessionGate>
				<RouterProvider router={router} />
			</SessionGate>
		</StoreProvider>
	</StrictMode>
);

/**
 * PWA — упаковка, а не вторая версия: тот же билд и тот же код. Регистрируем воркер
 * и молча забираем обновление — «приложение обновилось» это не событие, требующее
 * решения человека, а фон.
 */
function registerServiceWorker(): void {
	if (!("serviceWorker" in navigator) || import.meta.env.DEV) {
		return;
	}
	void import("@serwist/window").then(({ Serwist }) => {
		const serwist = new Serwist("/sw.js", { scope: "/", type: "classic" });
		serwist.addEventListener("waiting", () => {
			// Оболочка обновилась. Данные при этом не трогаются — они в IndexedDB.
			serwist.addEventListener("controlling", () => location.reload());
			void serwist.messageSkipWaiting();
		});
		void serwist.register();
	});
}

function restoreTheme(): void {
	try {
		const saved = localStorage.getItem("mapcraft:theme");
		// `ink`/`paper` — имена прошлого визуального языка; тихо переводим, чтобы
		// человек не обнаружил, что тема «слетела» после обновления.
		const theme = saved === "paper" ? "light" : saved === "ink" ? "dark" : saved;
		if (theme === "light" || theme === "dark") {
			document.documentElement.dataset.theme = theme;
		}
	} catch {
		// приватный режим — остаётся тема по умолчанию
	}
	// Безусловно: тема по умолчанию тоже обязана добраться до полосы состояния.
	syncStatusBarColor();
}
